import React from 'react';
import styled from 'styled-components';
import { MotionReveal } from '@components/motion/scroll-motion';
import { colors } from '@config/colors';
import { heightMedia, media } from '@config/breakpoints';
import { spacing } from '@config/spacing';
import { typography } from '@config/typography';
import { WishStory, byNight } from '@components/wish-story/wish-story';

/*
 * How we build: every wish a mom makes is reviewed and built into the next
 * answer — told as one wish, start to finish (see `wish-story/`), rather
 * than as a diagram of the steps. The story plays on the page's own
 * water, and at its night the page goes dark with it: the story sets
 * --how-night (0 day … 1 night) here, and the words turn to the light.
 */

type HowWeBuildProps = {
  /** Called each time the story finishes a telling — the page's autoplay moves on then. */
  onCycleEnd?: () => void;
  /** Called when the reader moves the story with its acts. */
  onSteer?: () => void;
};

export const HowWeBuild: React.FC<HowWeBuildProps> = ({ onCycleEnd, onSteer }) => (
  <Root>
    <Header>
      <MotionReveal>
        <Eyebrow>How we build</Eyebrow>
        <Heading>
          Every wish makes it better for the <Accent>next mom.</Accent>
        </Heading>
      </MotionReveal>
    </Header>

    <WishStory onCycleEnd={onCycleEnd} onSteer={onSteer} />
  </Root>
);

/* A short screen — a laptop, a tablet on its side, a small phone — can't
   give the story its height and the heading its two lines: the heading
   tightens, and the stage (which measures what the words leave, see
   `wish-story.tsx`) gets the room. */
const SHORT = heightMedia.short;

/* Wide screens give the story the page's wider column. */
const Root = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;

  ${media['3xl']} {
    max-width: 1480px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${spacing.lg};

  ${media.lg} {
    margin-bottom: ${spacing.xl};
  }
`;

const Eyebrow = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
  color: ${byNight(colors.brand.exxxtraDark, colors.brand.extraDark)};
  margin-bottom: ${spacing.md};
`;

/* Two balanced lines three across — one on a short screen — so the story
   below gets the room. */
const Heading = styled.h2`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(1.875rem, 4vw, 3.25rem);
  font-weight: 300;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${colors.neutral.extraDark};
  color: ${byNight(colors.neutral.extraDark, colors.white)};
  max-width: 17em;
  margin: 0 auto;
  text-wrap: balance;

  ${SHORT} {
    font-size: clamp(1.75rem, 2.6vw, 2.5rem);
    max-width: none;
  }
`;

/* The coral "next mom." wears during Behind the Scenes, coral.dark, kept
   the same all the way through (June, 2026-09-23); the active act's fill
   is the same coral. On the night's slate it reads 3.2:1; by day, on the
   water's own pixels, 1.5–1.8:1, well under the 3:1 AA asks of large text
   (flagged to June: on this pale water only a coral as deep as
   coral.exxxtraDark, the red she turned down, reaches 3:1). */
const Accent = styled.span`
  font-weight: 400;
  color: ${colors.accent.coral.dark};
`;
